import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setMainHeading } from "../../redux/modules/mainHeading/slice";
import { Box, Paper, Typography, Card, CardContent, Chip, Alert, useMediaQuery, useTheme } from "@mui/material";
import { getPatientDashboardData } from "../../redux/modules/dashboard/slice";
import { generateFullDate, getTime } from "../../helper/dateTime";
import EventIcon from '@mui/icons-material/Event';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';

const AppointmentCard = ({ appointment, isLast = false, isMobile = false }) => {
  const examNames = appointment?.examList
    ?.map((item) => item.examListModality?.map((listItem) => listItem?.examName))
    .flat()
    .filter(Boolean)
    .join(", ") || "Imaging Study";

  return (
    <Card sx={{ mb: 2, borderLeft: 4, borderColor: isLast ? 'grey.400' : 'primary.main' }}>
      <CardContent sx={{ px: isMobile ? 2 : 3 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box sx={{ flex: 1, minWidth: isMobile ? 0 : 200 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <EventIcon color="primary" fontSize="small" />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {generateFullDate(appointment?.appoinments?.startTime)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <AccessTimeIcon color="action" fontSize="small" />
              <Typography variant="body2" color="text.secondary">
                {getTime(appointment?.appoinments?.startTime)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {examNames.split(", ").map((exam, idx) => (
                <Chip key={idx} label={exam} size="small" variant="outlined" color="primary" />
              ))}
            </Box>
          </Box>
          <Box sx={{ flex: 1, minWidth: isMobile ? 0 : 200 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <LocalHospitalIcon color="action" fontSize="small" />
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {appointment?.branch?.name || 'N/A'}
              </Typography>
            </Box>
            {appointment?.branch?.phone && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <PhoneIcon color="action" fontSize="small" />
                <Typography variant="body2" color="text.secondary">
                  {appointment.branch.phone}
                </Typography>
              </Box>
            )}
            {appointment?.branch?.address && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <LocationOnIcon color="action" fontSize="small" sx={{ mt: 0.25 }} />
                <Typography variant="body2" color="text.secondary">
                  {appointment.branch.address}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const PatientDashboard = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { upcomingAppointments, pastAppointments } = useSelector(state => state.dashboard.patientDashboardData);
  const currentBranch = useSelector(state => state.branch.currentBranch);

  useEffect(() => {
    dispatch(setMainHeading("Welcome back!"));
    dispatch(getPatientDashboardData());
  }, [dispatch]);

  useEffect(() => {
    if (currentBranch?.id && currentBranch?.id.length > 0) {
      dispatch(getPatientDashboardData());
    }
  }, [dispatch, currentBranch?.id]);

  // Get the most recent past appointment (last appointment)
  const lastAppointment = pastAppointments && pastAppointments.length > 0
    ? [...pastAppointments].sort((a, b) =>
        new Date(b?.appoinments?.startTime) - new Date(a?.appoinments?.startTime)
      )[0]
    : null;

  // Sort upcoming appointments by date (soonest first)
  const sortedUpcoming = upcomingAppointments && upcomingAppointments.length > 0
    ? [...upcomingAppointments].sort((a, b) =>
        new Date(a?.appoinments?.startTime) - new Date(b?.appoinments?.startTime)
      )
    : [];

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.dark', mb: { xs: 2, sm: 3 }, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
        Dashboard
      </Typography>

      {/* Upcoming Appointments */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 4 } }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'primary.main', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
          Upcoming Appointments
        </Typography>
        {sortedUpcoming.length > 0 ? (
          sortedUpcoming.map((appointment) => (
            <AppointmentCard key={appointment._id} appointment={appointment} isMobile={isMobile} />
          ))
        ) : (
          <Alert severity="info" sx={{ mt: 1 }}>
            You have no upcoming appointments scheduled.
          </Alert>
        )}
      </Paper>

      {/* Last Appointment */}
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'text.secondary', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
          Last Appointment
        </Typography>
        {lastAppointment ? (
          <AppointmentCard appointment={lastAppointment} isLast={true} isMobile={isMobile} />
        ) : (
          <Alert severity="info" sx={{ mt: 1 }}>
            No previous appointments found.
          </Alert>
        )}
      </Paper>
    </Box>
  );
};

export default PatientDashboard;